/**
 * MYA Aplicaciones — Ordenamiento estándar de tablas
 * (copia del helper de erp-mya para mantener UX consistente en el ecosistema)
 *
 * Provee:
 *   - useTableSort<T>(): hook con estado de ordenamiento (key, direction).
 *   - sortRows(rows, sort, getValue): aplica el ordenamiento a un arreglo.
 *   - <SortableTh>: encabezado clickeable con flecha indicadora.
 *
 * Reglas:
 *   - Default es 'asc' al hacer primer clic. Segundo clic invierte a 'desc'.
 *   - number → comparación numérica. string → localeCompare con numeric.
 *   - null/undefined/'' siempre al final independientemente de la dirección.
 */
import { useCallback, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string = string> {
  key: K;
  direction: SortDirection;
}

export interface UseTableSortReturn<K extends string> {
  value: SortState<K>;
  set: (next: SortState<K>) => void;
  /** Clic en una columna: si es la misma key invierte; si es otra, vuelve a 'asc'. */
  toggle: (key: K) => void;
}

export function useTableSort<K extends string>(initial: SortState<K>): UseTableSortReturn<K> {
  const [value, setValue] = useState<SortState<K>>(initial);
  const toggle = useCallback((key: K) => {
    setValue((prev) => prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' });
  }, []);
  return { value, set: setValue, toggle };
}

/**
 * Aplica ordenamiento a un arreglo. No muta el original.
 * `getValue(row, key)` devuelve el valor a comparar (number, string, Date, null).
 */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  getValue: (row: T, key: K) => string | number | Date | null | undefined,
): T[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    const va = getValue(a, sort.key);
    const vb = getValue(b, sort.key);
    // null/undefined al final siempre
    const aEmpty = va === null || va === undefined || va === '';
    const bEmpty = vb === null || vb === undefined || vb === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    let result: number;
    if (typeof va === 'number' && typeof vb === 'number') {
      result = va - vb;
    } else if (va instanceof Date && vb instanceof Date) {
      result = va.getTime() - vb.getTime();
    } else {
      result = String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
    }
    return sort.direction === 'asc' ? result : -result;
  });
  return sorted;
}

/**
 * Encabezado de columna clickeable con indicador de orden.
 * Pensado para uso dentro de <th>:
 *   <th><SortableTh sort={sort} sortKey="num">Bloque</SortableTh></th>
 */
export function SortableTh<K extends string>({
  sort,
  sortKey,
  children,
  align = 'left',
  disabled = false,
}: {
  sort: UseTableSortReturn<K>;
  sortKey: K;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  disabled?: boolean;
}) {
  const isActive = sort.value.key === sortKey;
  const dir = isActive ? sort.value.direction : null;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <button
      type="button"
      onClick={() => !disabled && sort.toggle(sortKey)}
      disabled={disabled}
      className={`w-full inline-flex items-center gap-1 ${justify} cursor-pointer select-none transition-colors hover:text-blue-300 disabled:cursor-default disabled:hover:text-inherit ${isActive ? 'text-blue-400' : ''}`}
      title={`Ordenar por ${typeof children === 'string' ? children : 'esta columna'}`}
    >
      <span>{children}</span>
      <span className={`text-[9px] leading-none ${isActive ? 'opacity-100' : 'opacity-30'}`}>
        {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '⇅'}
      </span>
    </button>
  );
}
